import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { transformFromAlignedBox, type AlignedBox } from '.';
import { useCallback } from 'react';
import type { CameraTargetProps, Number3 } from '../types';

export const useGetLookAtCuboidTarget = (screenPaddingPercent: number = 5) => {
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);

  const getLookAtCuboidTarget = useCallback(
    (box: AlignedBox, cameraDirection: Number3) => {
      const transform = transformFromAlignedBox(box);

      // transform.position
      const positionVec = new THREE.Vector3(...transform.position);
      const cameraDirectionVec = new THREE.Vector3(...cameraDirection).normalize();
      const scaledCameraDirectionVec = cameraDirectionVec
        .clone()
        .multiplyScalar(Math.max(...transform.scale) * 2);
      const cameraPositionVec = positionVec.clone().sub(scaledCameraDirectionVec);

      const tmpCamera = camera.clone();
      tmpCamera.position.copy(cameraPositionVec);
      tmpCamera.lookAt(positionVec);
      tmpCamera.updateMatrixWorld();

      const bbox = new THREE.Box3(new THREE.Vector3(...box.min), new THREE.Vector3(...box.max));
      bbox.applyMatrix4(tmpCamera.matrixWorldInverse);

      const sizeX = bbox.max.x - bbox.min.x;
      const sizeY = bbox.max.y - bbox.min.y;

      const paddingRatio = 1 - 2 * screenPaddingPercent * 0.01;

      if (camera instanceof THREE.OrthographicCamera) {
        const sensorX = camera.right - camera.left;
        const sensorY = camera.top - camera.bottom;

        const zoomX = sensorX / sizeX;
        const zoomY = sensorY / sizeY;

        const zoom = paddingRatio * Math.min(zoomX, zoomY);

        // console.log('zoom', { sizeX, sizeY, sensorX, sensorY, zoomX, zoomY, zoom });
        const cameraTraget: CameraTargetProps = {
          position: cameraPositionVec.toArray(),
          lookAt: positionVec.toArray(),
          zoom,
          transitionTime: 0,
        };
        return cameraTraget;
      } else {
        const screenSize = new THREE.Vector2();
        gl.getSize(screenSize);

        const slopeY = Math.tan((camera.fov * 0.5 * Math.PI) / 180);
        const distanceY = ((sizeY / paddingRatio) * 0.5) / slopeY;

        const slopeX = (slopeY / screenSize.y) * screenSize.x;
        const distanceX = (paddingRatio * sizeX * 0.5) / slopeX;

        const distance = Math.max(distanceX, distanceY);
        cameraDirectionVec.normalize().multiplyScalar(distance);
        const cameraPositionVec = positionVec.clone().sub(cameraDirectionVec);

        // console.log('distance', {
        //   fov: camera.fov,
        //   distance,
        //   distanceX,
        //   distanceY,
        //   slopeX,
        //   slopeY,
        //   cameraDirection: cameraDirectionVec.toArray(),
        //   screenSize: screenSize.toArray(),
        // });

        const cameraTraget: CameraTargetProps = {
          position: cameraPositionVec.toArray(),
          lookAt: positionVec.toArray(),
          transitionTime: 0,
        };
        return cameraTraget;
      }
    },
    [camera, screenPaddingPercent, gl]
  );

  return getLookAtCuboidTarget;
};
